import { useState, useMemo, useCallback } from "react";
import {
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
  differenceInDays,
} from "date-fns";

// ============================================================================
// Types
// ============================================================================

export type ZoomLevel = 'day' | 'week' | 'month';

export interface DateRange {
  start: Date;
  end: Date;
}

export interface TankPlanningState {
  currentDate: Date;
  zoomLevel: ZoomLevel;
  dateRange: DateRange;
  dayCount: number;
  dayWidth: number;
  vesselTypeFilter: string;
  setZoomLevel: (level: ZoomLevel) => void;
  setVesselTypeFilter: (filter: string) => void;
  navigate: (direction: 'prev' | 'next' | 'today') => void;
  goToDate: (date: Date) => void;
  extendEnd: () => void;
  extendStart: () => void;
}

// ============================================================================
// Constants
// ============================================================================

const DAY_WIDTH: Record<ZoomLevel, number> = {
  day: 80,    // Each day is 80px wide - detailed view
  week: 40,   // Each day is 40px - weekly overview
  month: 16,  // Each day is 16px - long-term view
};

// ============================================================================
// Hook
// ============================================================================

export function useTankPlanningState(): TankPlanningState {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>('week');
  const [vesselTypeFilter, setVesselTypeFilter] = useState<string>('all');
  const [extraDays, setExtraDays] = useState(0);
  const [extraStartDays, setExtraStartDays] = useState(0);

  // Calculate date range based on zoom level, extended by any scroll-triggered extra days
  const dateRange = useMemo((): DateRange => {
    let base: DateRange;
    switch (zoomLevel) {
      case 'day':
        // Show 2 weeks centered on current date
        base = {
          start: subDays(currentDate, 7),
          end: addDays(currentDate, 7),
        };
        break;
      case 'week':
        // Show 5 weeks: 1 week back, 4 weeks forward (~35 days)
        base = {
          start: startOfWeek(subWeeks(currentDate, 1), { weekStartsOn: 1 }),
          end: endOfWeek(addWeeks(currentDate, 4), { weekStartsOn: 1 }),
        };
        break;
      case 'month':
      default:
        // Show 6 months: 1 month back, 5 months forward
        base = {
          start: startOfMonth(subMonths(currentDate, 1)),
          end: endOfMonth(addMonths(currentDate, 4)),
        };
    }
    return {
      start: subDays(base.start, extraStartDays),
      end: addDays(base.end, extraDays),
    };
  }, [currentDate, zoomLevel, extraDays, extraStartDays]);

  // Calculate day count and width
  const dayCount = useMemo(() => {
    return differenceInDays(dateRange.end, dateRange.start) + 1;
  }, [dateRange]);

  const dayWidth = DAY_WIDTH[zoomLevel];

  // Extend the timeline end when scrolling near the right edge (max 2 years total extra)
  const extendEnd = useCallback(() => {
    setExtraDays(prev => {
      const increment = zoomLevel === 'day' ? 14 : zoomLevel === 'week' ? 28 : 60;
      return Math.min(prev + increment, 730);
    });
  }, [zoomLevel]);

  // Extend the timeline start when scrolling near the left edge (max 2 years total extra)
  const extendStart = useCallback(() => {
    setExtraStartDays(prev => {
      const increment = zoomLevel === 'day' ? 14 : zoomLevel === 'week' ? 28 : 60;
      return Math.min(prev + increment, 730);
    });
  }, [zoomLevel]);

  // Navigation resets the scroll extension so the view is crisp again
  const navigate = useCallback((direction: 'prev' | 'next' | 'today') => {
    setExtraDays(0);
    setExtraStartDays(0);
    if (direction === 'today') {
      setCurrentDate(new Date());
      return;
    }

    const delta = direction === 'next' ? 1 : -1;
    setCurrentDate((current) => {
      switch (zoomLevel) {
        case 'day':
          return addDays(current, delta * 7);
        case 'week':
          return addWeeks(current, delta * 4);
        case 'month':
          return addMonths(current, delta * 2);
        default:
          return current;
      }
    });
  }, [zoomLevel]);

  // Go to specific date
  const goToDate = useCallback((date: Date) => {
    setExtraDays(0);
    setExtraStartDays(0);
    setCurrentDate(date);
  }, []);

  return {
    currentDate,
    zoomLevel,
    dateRange,
    dayCount,
    dayWidth,
    vesselTypeFilter,
    setZoomLevel,
    setVesselTypeFilter,
    navigate,
    goToDate,
    extendEnd,
    extendStart,
  };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Calculate the left position (in pixels) for a date within the timeline
 */
export function calculateDatePosition(
  date: Date,
  startDate: Date,
  dayWidth: number
): number {
  const days = differenceInDays(date, startDate);
  return days * dayWidth;
}

/**
 * Calculate the width (in pixels) for a date range
 */
export function calculateRangeWidth(
  startDate: Date,
  endDate: Date | null,
  dayWidth: number,
  timelineEndDate: Date
): number {
  const effectiveEndDate = endDate || timelineEndDate;
  const days = differenceInDays(effectiveEndDate, startDate) + 1;
  return Math.max(days * dayWidth, dayWidth); // At least 1 day wide
}

/**
 * Calculate the number of days from a pixel delta
 */
export function calculateDaysDelta(
  deltaX: number,
  dayWidth: number
): number {
  return Math.round(deltaX / dayWidth);
}

/**
 * Check if a date falls within a range
 */
export function isDateInRange(
  date: Date,
  rangeStart: Date,
  rangeEnd: Date
): boolean {
  return date >= rangeStart && date <= rangeEnd;
}
